import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceDot,
  ReferenceLine,
} from 'recharts';
import type { Forecast } from '../types';

interface Props {
  forecast: Forecast;
}

interface ChartDatum {
  day: number;
  label: string;
  楽観: number;
  標準: number;
  保守: number;
}

const ForecastChart: React.FC<Props> = ({ forecast }) => {
  if (!forecast || forecast.points.length === 0) return null;

  const data: ChartDatum[] = forecast.points.map((p) => ({
    day: p.day,
    label: p.label,
    楽観: p.optimistic_rank,
    標準: p.standard_rank,
    保守: p.conservative_rank,
  }));

  // Compute Y axis domain - rank goes lower-better, so we reverse it.
  const maxRank = Math.max(...data.flatMap((d) => [d.楽観, d.標準, d.保守]));
  const yDomain: [number, number] = [Math.max(1, maxRank + 2), 1];

  return (
    <section className="py-4 border-b border-slate-200">
      <div className="flex items-baseline justify-between mb-1 flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
          期待される順位推移
        </h2>
        <span className="text-xs text-slate-500">
          現在 {forecast.current_rank} 位 / スコア {forecast.current_score} pt
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        業界平均ベースの予測。3シナリオ（楽観・標準・保守）で示しています。
        受注後は実測順位を緑色のラインで上書き表示します。
      </p>

      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 10, right: 24, bottom: 24, left: 0 }}>
            <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
            <XAxis
              dataKey="day"
              type="number"
              domain={[0, 180]}
              ticks={[0, 30, 60, 90, 180]}
              tickFormatter={(v) => `${v}日`}
              tick={{ fontSize: 11, fill: '#6b7280' }}
              axisLine={{ stroke: '#cbd5e1' }}
              tickLine={{ stroke: '#cbd5e1' }}
            >
              <text>日数</text>
            </XAxis>
            <YAxis
              domain={yDomain}
              tickFormatter={(v) => `${v}位`}
              tick={{ fontSize: 11, fill: '#6b7280' }}
              axisLine={{ stroke: '#cbd5e1' }}
              tickLine={{ stroke: '#cbd5e1' }}
              width={50}
              reversed={false}
            />
            <Tooltip
              contentStyle={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                fontSize: 12,
              }}
              labelFormatter={(v) => `${v}日後`}
              formatter={(value: number) => `${value}位`}
            />
            <Legend
              wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
              iconType="line"
            />
            <ReferenceLine y={3} stroke="#76C043" strokeDasharray="4 4" label={{ value: '3-pack 圏内', position: 'right', fill: '#76C043', fontSize: 11 }} />
            <Line type="monotone" dataKey="楽観" stroke="#76C043" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="標準" stroke="#1E2D45" strokeWidth={3} dot={{ r: 4 }} />
            <Line type="monotone" dataKey="保守" stroke="#8B95A4" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
            {forecast.milestones.map((m) => (
              <ReferenceDot
                key={m.day}
                x={m.day}
                y={m.target_rank}
                r={5}
                fill="#f59e0b"
                stroke="#fff"
                strokeWidth={2}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
        {forecast.milestones.map((m) => (
          <div key={m.day} className="border border-slate-200 rounded-lg p-3 bg-amber-50/40">
            <div className="text-[11px] text-amber-700 font-semibold">Day {m.day}</div>
            <div className="text-sm text-slate-900 mt-0.5 leading-snug">{m.label}</div>
            <div className="text-xs text-slate-500 mt-1">目標 {m.target_rank} 位以内</div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ForecastChart;
